import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Document, Page, Text, View, Image, StyleSheet, PDFDownloadLink } from "@react-pdf/renderer";
import { DownloadSimple, MapPin, CalendarBlank, CheckCircle } from "@phosphor-icons/react";
import { formatIDR, formatDateTime } from "@/lib/api";

const styles = StyleSheet.create({
  page: { backgroundColor: "#050505", padding: 30, fontFamily: "Helvetica" },
  card: { border: "1pt solid #222", borderRadius: 8, overflow: "hidden" },
  header: { backgroundColor: "#00F0FF", padding: 16 },
  brand: { color: "#000", fontSize: 20, fontFamily: "Helvetica-Bold" },
  body: { backgroundColor: "#111", padding: 20, flexDirection: "row", justifyContent: "space-between" },
  eventName: { color: "#fff", fontSize: 18, fontFamily: "Helvetica-Bold", marginBottom: 8 },
  meta: { color: "#aaa", fontSize: 10, marginBottom: 4 },
  label: { color: "#00F0FF", fontSize: 8, marginTop: 10, fontFamily: "Helvetica-Bold" },
  value: { color: "#fff", fontSize: 12, marginBottom: 2 },
  qr: { width: 130, height: 130, backgroundColor: "#fff", padding: 6 },
  token: { color: "#555", fontSize: 7, marginTop: 20 },
});

function TicketPDF({ ticket, qr }) {
  return (
    <Document>
      <Page size="A5" style={styles.page}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.brand}>EVENTKU · E-TICKET</Text>
          </View>
          <View style={styles.body}>
            <View style={{ flex: 1, paddingRight: 12 }}>
              <Text style={styles.eventName}>{ticket.event_name}</Text>
              <Text style={styles.meta}>{ticket.event_date}</Text>
              <Text style={styles.meta}>{ticket.event_location}</Text>
              <Text style={styles.label}>PENGUNJUNG</Text>
              <Text style={styles.value}>{ticket.attendee_name}</Text>
              <Text style={styles.label}>JENIS TIKET</Text>
              <Text style={styles.value}>{ticket.ticket_type_name}</Text>
              <Text style={styles.token}>{ticket.token}</Text>
            </View>
            {qr && <Image style={styles.qr} src={qr} />}
          </View>
        </View>
      </Page>
    </Document>
  );
}

export function TicketCard({ ticket }) {
  const [qr, setQr] = useState(null);
  const attended = ticket.status === "attended";

  useEffect(() => {
    QRCode.toDataURL(ticket.token, { width: 300, margin: 1 }).then(setQr);
  }, [ticket.token]);

  return (
    <div data-testid={`ticket-card-${ticket.id}`} className="bg-card border border-white/10 overflow-hidden hover:neon-glow transition-shadow duration-300">
      <div className="flex flex-col sm:flex-row">
        <div className="flex-1 p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] bg-primary text-black px-2 py-0.5">
              {ticket.ticket_type_name}
            </span>
            {attended ? (
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                <CheckCircle weight="fill" size={13} /> Sudah masuk
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#00FF66]">● Aktif</span>
            )}
          </div>
          <h3 className="font-heading font-bold text-xl text-white leading-tight mb-3">{ticket.event_name}</h3>
          <p className="text-sm text-zinc-400 flex items-center gap-1.5 mb-1">
            <CalendarBlank size={15} className="text-primary" /> {formatDateTime(ticket.event_date)}
          </p>
          <p className="text-sm text-zinc-400 flex items-center gap-1.5 mb-4">
            <MapPin size={15} className="text-primary" /> {ticket.event_location}
          </p>
          <p className="text-xs text-zinc-600">Pengunjung: <span className="text-zinc-300">{ticket.attendee_name}</span></p>
          {qr && (
            <PDFDownloadLink
              document={<TicketPDF ticket={ticket} qr={qr} />}
              fileName={`eticket-${ticket.token.slice(0, 8)}.pdf`}
            >
              {({ loading }) => (
                <button
                  data-testid={`download-pdf-${ticket.id}`}
                  className="mt-4 inline-flex items-center gap-2 bg-white/5 hover:bg-primary hover:text-black text-white text-sm font-semibold px-4 py-2 border border-white/15 transition-colors"
                >
                  <DownloadSimple size={16} weight="bold" /> {loading ? "Menyiapkan..." : "Cetak Tiket (PDF)"}
                </button>
              )}
            </PDFDownloadLink>
          )}
        </div>
        <div className="border-t sm:border-t-0 sm:border-l border-dashed border-white/15 p-6 flex flex-col items-center justify-center bg-black/30">
          {qr ? (
            <img data-testid={`ticket-qr-${ticket.id}`} src={qr} alt="QR" className={`w-36 h-36 bg-white p-1.5 ${attended ? "opacity-40 grayscale" : ""}`} />
          ) : (
            <div className="w-36 h-36 bg-white/5 animate-pulse" />
          )}
          <p className="text-[9px] text-zinc-600 mt-2 font-mono">{ticket.token.slice(0, 12)}</p>
        </div>
      </div>
    </div>
  );
}
